import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import GetAppIcon from '@material-ui/icons/GetApp';
import fileIcon from './fileIcon';
import callApi from '../../actions/callApi';
import CustomPropTypes from '../../helpers/CustomPropTypes';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const thresh = 1000;
  const sizesUnits = ['B', 'KB', 'MB'];
  let i = 0;
  let value = Math.abs(bytes);

  while (value >= thresh && i < sizesUnits.length - 1) {
    value /= thresh;
    i += 1;
  }
  const fixed = i < 2 ? 0 : 2;
  return `${value.toFixed(fixed)} ${sizesUnits[i]}`;
};

const useStyles = makeStyles((theme) => ({
  attachment: {
    width: '100%',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.common.white,
    borderRadius: 10,
    boxShadow: theme.customShadows.small,
    minHeight: theme.spacing(10),
  },
  mobileAttachment: {
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  fileIcon: {
    height: 34,
    color: theme.palette.darkGrey.main,
    marginRight: theme.spacing(1.5),
  },
  mainSection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  fileSection: {
    display: 'flex',
    width: '100%',
    minWidth: 100,
    marginRight: theme.spacing(1),
    alignItems: 'center',
  },
  fileDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  fileName: {
    marginBottom: theme.spacing(0.5),
    fontSize: 14,
    fontWeight: 'bold',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  fileSize: {
    color: theme.palette.darkGrey.light,
    fontSize: 12,
    fontWeight: 600,
  },
  fileDescription: {
    color: theme.palette.darkGrey.light,
    fontSize: 13,
    width: '100%',
  },
  readMore: {
    whiteSpace: 'pre',
    fontWeight: 600,
    fontSize: 13,
    display: 'inline-block',
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  link: {
    color: theme.palette.primary.light,
    textDecoration: 'none',
  },
  tooltipProperties: {
    fontSize: 12,
  },
  downloadIcon: {
    color: theme.palette.primary.main,
  },
}));

const AttachmentFile = ({ file, callApi: dispatchCallApi }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation('sponsors');
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [fileSize, setFileSize] = useState(null);
  const { description } = file;

  const { eventUri } = useParams();
  const markAsDownloaded = () => {
    const { eventFileCategory: { id: fileCategoryId }, id: fileId } = file;
    dispatchCallApi('markAsDownloaded', { eventUri, fileCategoryId, fileId });
  };

  const handleDownloadFile = () => {
    markAsDownloaded();
    window.open(file.eventFile, '_blank');
  };

  useEffect(() => {
    if (!file.eventFile) return;
    fetch(file.eventFile, { method: 'HEAD' })
      .then((res) => {
        const length = res.headers.get('content-length');
        setFileSize(formatFileSize(length ? parseInt(length, 10) : 0));
      })
      .catch((error) => console.log(error.message));
  }, [file.eventFile]);

  const extension = file.eventFile?.substring(file.eventFile.lastIndexOf('.') + 1, file.eventFile.length).split('?')[0] || file.eventFile;
  return (
    <div className={clsx(classes.attachment, mobile && classes.mobileAttachment)}>
      <div className={classes.mainSection}>
        <div className={classes.fileSection}>
          <img
            src={fileIcon[extension] ? fileIcon[extension].icon : fileIcon.other.icon}
            alt={fileIcon[extension] ? fileIcon[extension].name : fileIcon.other.name}
            className={classes.fileIcon}
          />
          <div className={classes.fileDetails}>
            <Typography className={classes.fileName} variant="h5">
              <a className={classes.link} onClick={markAsDownloaded} href={file.eventFile} rel="noopener noreferrer" target="_blank">
                {file.name}
              </a>
            </Typography>
            <Typography className={classes.fileSize} variant="h5">
              {fileSize}
            </Typography>
          </div>
        </div>
        <IconButton onClick={handleDownloadFile}>
          <GetAppIcon className={classes.downloadIcon} />
        </IconButton>
      </div>
      <div className={classes.fileDescription}>
        {description.length > 250 && description.length > 270 && !open ? (
          <>
            {description.slice(0, 250)}
            <span className={classes.readMore} onClick={() => setOpen(!open)} aria-hidden="true">{` ...${t('data.actions.readMore')}`}</span>
          </>
        ) : (description)}
      </div>
    </div>
  );
};

AttachmentFile.propTypes = {
  file: CustomPropTypes.eventFile.isRequired,
  callApi: PropTypes.func.isRequired,
};

export default connect(null, { callApi })(AttachmentFile);
